import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Calendar as CalendarIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const CalendarView = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/calendar/${year}/${month + 1}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error("Failed to fetch calendar data");
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Monday = 0
  };

  const monthNames = [
    "Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος",
    "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος",
    "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"
  ];

  const dayNames = ["Δευ", "Τρι", "Τετ", "Πεμ", "Παρ", "Σαβ", "Κυρ"];

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDate(null);
  };

  const formatDateKey = (day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  const getDeadlinesForDay = (day) => {
    const dateKey = formatDateKey(day);
    const dayData = calendarData[dateKey];
    if (!dayData) return { items: [], total: 0 };
    // Backward compat: αν είναι array (παλιά μορφή), wrap το.
    if (Array.isArray(dayData)) return { items: dayData, total: dayData.length };
    // Νέα μορφή: { items: [...], total: N }
    return {
      items: Array.isArray(dayData.items) ? dayData.items : [],
      total: typeof dayData.total === "number" ? dayData.total : (dayData.items?.length || 0),
    };
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-24 md:h-32 bg-slate-50/50"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = getDeadlinesForDay(day);
      const items = dayData.items;
      const total = dayData.total;
      const hasDeadlines = total > 0;
      const dateKey = formatDateKey(day);
      const isSelected = selectedDate === dateKey;

      days.push(
        <div
          key={day}
          className={`h-24 md:h-32 border border-slate-100 p-1 md:p-2 calendar-day transition-colors ${
            isToday(day) ? "bg-primary/5 border-primary/20" : "bg-white"
          } ${isSelected ? "ring-2 ring-primary" : ""} ${
            hasDeadlines ? "cursor-pointer" : ""
          }`}
          onClick={() => hasDeadlines && setSelectedDate(isSelected ? null : dateKey)}
          data-testid={`calendar-day-${day}`}
        >
          <div className="flex items-start justify-between">
            <span className={`text-sm font-medium ${
              isToday(day) ? "text-primary" : "text-slate-700"
            }`}>
              {day}
            </span>
            {hasDeadlines && (
              <Badge className="text-xs px-1.5 py-0 bg-primary text-white">
                {total}
              </Badge>
            )}
          </div>
          
          {/* Deadline indicators */}
          <div className="mt-1 space-y-0.5 overflow-hidden">
            {items.slice(0, 2).map((deadline, idx) => (
              <TooltipProvider key={idx}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`text-xs truncate px-1 py-0.5 rounded ${
                      deadline.deadline_type === "proposals" ? "bg-blue-100 text-blue-700" :
                      deadline.deadline_type === "addition" ? "bg-purple-100 text-purple-700" :
                      deadline.deadline_type === "judge_assignment" ? "bg-amber-100 text-amber-700" :
                      "bg-emerald-100 text-emerald-700"
                    }`}>
                      {deadline.gak_number}/{deadline.gak_year}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{deadline.deadline_title}</p>
                    <p className="text-xs">ΓΑΚ {deadline.gak_number}/{deadline.gak_year} · ΕΑΚ {deadline.eak_number || "—"}/{deadline.gak_year}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            {total > 2 && (
              <span className="text-xs text-slate-500">+{total - 2} ακόμα</span>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const _selectedDayData = selectedDate ? calendarData[selectedDate] : null;
  const selectedDeadlines = _selectedDayData
    ? (Array.isArray(_selectedDayData) ? _selectedDayData : (_selectedDayData.items || []))
    : [];
  const selectedTotal = _selectedDayData
    ? (Array.isArray(_selectedDayData) ? _selectedDayData.length : (_selectedDayData.total || 0))
    : 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ημερολόγιο</h1>
          <p className="text-slate-500 mt-1">Προβολή προθεσμιών ανά ημέρα</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle className="text-xl">
                {monthNames[month]} {year}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[500px] w-full" />
            ) : (
              <>
                {/* Day names */}
                <div className="grid grid-cols-7 mb-2">
                  {dayNames.map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                  {renderCalendarDays()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarIcon className="w-5 h-5" />
              {selectedDate ? (
                new Date(selectedDate).toLocaleDateString("el-GR", {
                  weekday: "long",
                  day: "numeric",
                  month: "long"
                })
              ) : (
                "Επιλέξτε ημέρα"
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <p className="text-sm text-slate-500 text-center py-8">
                Κάντε κλικ σε μια ημέρα με προθεσμίες για να δείτε λεπτομέρειες
              </p>
            ) : selectedDeadlines.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                Δεν υπάρχουν προθεσμίες
              </p>
            ) : (
              <div className="space-y-3">
                {selectedTotal > selectedDeadlines.length && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Εμφανίζονται οι πρώτες <strong>{selectedDeadlines.length}</strong> από συνολικά <strong>{selectedTotal}</strong> προθεσμίες αυτής της ημέρας.
                  </div>
                )}
                {selectedDeadlines.map((deadline, idx) => (
                  <Link
                    key={`${deadline.case_id}-${idx}`}
                    to={`/cases/${deadline.case_id}`}
                    className={`block p-3 rounded-lg transition-colors ${
                      deadline.deadline_type === "proposals" ? "bg-blue-50 hover:bg-blue-100" :
                      deadline.deadline_type === "addition" ? "bg-purple-50 hover:bg-purple-100" :
                      deadline.deadline_type === "judge_assignment" ? "bg-amber-50 hover:bg-amber-100" :
                      "bg-emerald-50 hover:bg-emerald-100"
                    }`}
                    data-testid={`deadline-detail-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-mono text-sm font-semibold text-slate-900">
                          ΓΑΚ {deadline.gak_number}/{deadline.gak_year}
                        </p>
                        <p className="font-mono text-xs text-blue-700">
                          ΕΑΚ {deadline.eak_number || "—"}/{deadline.gak_year}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          {deadline.deadline_title}
                        </p>
                      </div>
                      <Badge className={deadline.court_type === "Μονομελές" ? "court-mono" : "court-poly"}>
                        {deadline.court_type?.charAt(0)}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-slate-500">Τύποι Προθεσμιών:</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span>Κατάθεση Προτάσεων</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-purple-500"></div>
              <span>Προσθήκη-Αντίκρουση</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-500"></div>
              <span>Ορισμός Δικαστή</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500"></div>
              <span>Προδικαστικός Έλεγχος</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarView;
